import { X } from "lucide-react";
import React from "react";

import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { useTheme } from "@mui/material";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Slide from "@mui/material/Slide";
import Stack from "@mui/material/Stack";

export interface ChoiceSheetOption {
  label: string;
  value: string;
}

interface ChoiceSheetProps {
  open: boolean;
  title?: string;
  options: ChoiceSheetOption[];
  onSelect: (option: ChoiceSheetOption) => void;
  onCancel: () => void;
}

/**
 * Generic bottom-sheet picker. Slides up over the panel body and lists a small
 * set of options to choose from, with a cancel affordance. Purely
 * presentational — the consumer owns what select/cancel mean (e.g. Viv feeds
 * them into the navigate continuation). Anchors to the nearest positioned
 * ancestor, so its host must be `position: relative`.
 */
export const ChoiceSheet = ({
  open,
  title,
  options,
  onSelect,
  onCancel,
}: ChoiceSheetProps) => {
  // IgButton merges `sx` via object spread (`{ ...sx }`), which silently drops
  // a function-form `sx` — so read the theme here and pass a plain object.
  const theme = useTheme();

  return (
    <Slide direction="up" in={open} mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        sx={{
          position: "absolute",
          insetInline: 0,
          bottom: 0,
          zIndex: 10,
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          maxHeight: "70%",
          overflowY: "auto",
          minHeight: 300,
        }}
      >
        <IgTypography
          component="div"
          sx={{ px: 4, pt: 4, pb: 2 }}
          variant="subtitle1"
        >
          {title ?? "Which one?"}
        </IgTypography>
        <IconButton
          sx={{ position: "absolute", top: 8, right: 8 }}
          size="small"
          aria-label="Cancel"
          onClick={onCancel}
        >
          <X size={16} />
        </IconButton>
        <Stack divider={<Divider />}>
          {options.map((option, index) => (
            <IgButton
              key={option.value}
              variant="text"
              color="inherit"
              fullWidth
              onClick={() => onSelect(option)}
              sx={{
                justifyContent: "flex-start",
                textAlign: "left",
                textTransform: "none",
                fontWeight: "normal",
                borderRadius: 0,
                px: 4,
                py: 2,
                gap: 1,
                minHeight: 0,
                "&:hover": {
                  backgroundColor:
                    theme.igBackgroundColors.interactive.neutral.active,
                },
              }}
            >
              <IgTypography
                component="div"
                variant="body2"
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: "100%",
                  color: "text.secondary",
                  fontWeight: "bold",
                  backgroundColor:
                    theme.igBackgroundColors.interactive.neutral.default,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {index + 1}
              </IgTypography>
              <IgTypography
                component="div"
                variant="body2"
                sx={{ flex: 1, textAlign: "left" }}
              >
                {option.label}
              </IgTypography>
            </IgButton>
          ))}
        </Stack>
      </Paper>
    </Slide>
  );
};
